'use client';

import React from 'react';
import {
  isNewsEmojiCode,
  getNewsEmojiUrl,
  getNewsEmojiFilter,
  getNewsEmojiLabel,
} from '@/components/KnowledgeBaseView/news-emojis';

interface NewsEmojiInlineProps {
  /**
   * Значение поля `document_meta.icon`:
   *   - unicode-эмодзи (например, "🚀") — рендерится как текст,
   *   - news-код (например, "e-rocket:r" или "e-bird") — рендерится как <img> с
   *     CSS-filter для смены цвета (см. kb-news-pages.md → Фирменные SVG-эмодзи),
   *   - null/пусто — не рендерится.
   */
  value: string | null | undefined;
  /** Размер картинки/шрифта. По умолчанию 18px (как в карточке ленты). */
  size?: number;
  className?: string;
}

/**
 * Унифицированный inline-рендерер news-эмодзи. Используется в карточке ленты,
 * в featured-блоке hero и везде, где нужно показать иконку news-документа.
 *
 * Без этого компонента news-код `e-bird:r` показывался как plain-text — баг,
 * замеченный в Phase 2 при первой E2E-проверке.
 */
export function NewsEmojiInline({
  value,
  size = 18,
  className,
}: NewsEmojiInlineProps): React.ReactElement | null {
  if (!value) return null;

  if (isNewsEmojiCode(value)) {
    const url = getNewsEmojiUrl(value);
    if (!url) return null;
    const filter = getNewsEmojiFilter(value);
    const label = getNewsEmojiLabel(value) ?? '';
    return (
      /* eslint-disable-next-line @next/next/no-img-element */
      <img
        src={url}
        alt={label}
        aria-label={label}
        className={className}
        style={{
          width: size,
          height: size,
          display: 'inline-block',
          objectFit: 'contain',
          filter: filter || undefined,
          flexShrink: 0,
        }}
      />
    );
  }

  // Unicode-эмодзи — просто текст.
  return (
    <span
      className={className}
      style={{
        fontSize: size,
        lineHeight: 1,
        display: 'inline-block',
        flexShrink: 0,
      }}
    >
      {value}
    </span>
  );
}
